<script lang="ts" setup>
import type { IconVariantType, VariantType } from '#components/elements/input';
import ErrorText from '#components/elements/input/inputText/ErrorText.vue';
import type { IconName } from '#components/icons/icon.model';
import { useVModel } from '@vueuse/core';
import type { PropType, Ref } from 'vue';
import { computed, ref } from 'vue';
import InputBase from '../InputBase.vue';

const props = defineProps({
  name: { type: String, required: true },
  type: { type: String, default: 'text' },
  variant: { type: String as PropType<VariantType>, default: 'default' },
  label: { type: String as PropType<string | null>, required: false, default: undefined },
  autocomplete: {
    type: String as PropType<string | undefined>,
    required: false,
    default: undefined
  },
  placeholder: { type: String, required: true },
  modelValue: { type: String, default: undefined },
  message: { type: String, required: false, default: undefined },
  required: { type: Boolean, default: false },
  icon: { type: String as PropType<IconName>, required: false, default: undefined },
  iconVariant: { type: String as PropType<IconVariantType>, default: 'default' },
  disabled: { type: Boolean, default: false },
  maxlength: { type: Number, default: undefined },
  errorText: { type: String as PropType<string>, required: false, default: undefined },
  suffixText: { type: String as PropType<string>, required: false, default: undefined },
  cssClass: { type: String, required: false, default: undefined }
});
const inputRef: Ref<HTMLInputElement | undefined> = ref(undefined);
const emit = defineEmits<{
  'update:modelValue': [value: string | undefined];
}>();

const inputClx = computed(() => ({
  'text-default-disabled': props.disabled
}));

const value = useVModel(props, 'modelValue');

function handleEscape(event: KeyboardEvent) {
  if (props.type === 'search') {
    emit('update:modelValue', '');
    const el = inputRef.value as HTMLInputElement;
    el.blur();
    event.preventDefault();
    event.stopPropagation();
  }
}
</script>

<template>
  <div :class="cssClass">
    <InputBase v-bind="{ variant, label, message, required, icon, iconVariant, disabled, inputRef }">
      <template #icon>
        <slot name="icon" />
      </template>
      <template #default="scope">
        <input
          v-bind="{ type, placeholder, name, disabled, maxlength }"
          :id="name"
          ref="inputRef"
          v-model="value"
          :class="[inputClx, { 'mr-20': !!suffixText }]"
          :autocomplete="autocomplete"
          class="w-full h-14 outline-none px-6 p-0 border-none focus:ring-0 rounded placeholder-default-lighter"
          @focus="scope.focus"
          @blur="scope.blur"
          @keydown.escape="handleEscape"
        />
        <span v-if="suffixText" class="absolute inset-y-0 right-0 flex items-center pr-8 text-xl text-gray-[#575757]">{{
          suffixText
        }}</span>
      </template>
    </InputBase>
    <ErrorText v-if="errorText" :text="errorText" :test-id="`input-base-${props.label}`" />
  </div>
</template>
